import confetti from 'canvas-confetti';
import React, {Dispatch, SetStateAction, useEffect, useState} from 'react';

import {Circle} from '#Client/components/Circle/Circle';
import {DateCalendar} from '#Client/components/DateCalendar/DateCalendar';
import {IconButton} from '#Client/components/IconButton/IconButton';
import {StarGroup} from '#Client/components/StarGroup/StarGroup';
import {difficultyLabelMap, trackEvent} from '#Client/components/util';
import {
  buttonContainer,
  clipboardMessage,
  container,
  mainInfo,
  scoresLabel,
  stars,
  statLabel,
  statsContainer,
  statValue,
  streakBar,
  streakBarCurrent,
  streakLabel,
  streakLabelCurrent,
  streakTable,
  streakValue,
  victoryStatsContainer,
  zeroWidth,
} from '#Client/components/Victory/Victory.css';
import {Check} from '#Client/images/Check';
import {OPENED_MODAL, Stats} from '#Common/types';

type VictoryProps = {
  difficulty: number;
  fixedCells: Record<string, string>;
  puzzleDate: string;
  puzzleNumber: number;
  updateDifficulty: (newDifficulty: number) => void;
  gameStats: number[];
  myStats: Stats;
  onSetOpenedModal: Dispatch<SetStateAction<OPENED_MODAL>>;
};

const star = String.fromCodePoint(0x1f31f);

const phraseMap: Record<number, string> = {
  3: `Unbelievable!`,
  5: `Tremendous!`,
  7: `Awesome!`,
  9: `Nice job!`,
};

const particleMap: Record<number, number> = {
  3: 1000,
  5: 500,
  7: 200,
  9: 100,
};

export const Victory = ({
  difficulty,
  fixedCells,
  puzzleDate,
  puzzleNumber,
  gameStats,
  myStats,
  updateDifficulty,
  onSetOpenedModal,
}: VictoryProps) => {
  const percentBetterThan = Math.floor(
    (100 * (gameStats[1] - gameStats[0])) / gameStats[1],
  );
  const [copiedToClipboard, setCopiedToClipboard] = useState<boolean>(false);
  useEffect(() => {
    const particleCount = particleMap[difficulty];
    const end = Date.now() + particleMap[difficulty];

    if (difficulty === 3) {
      (function frame() {
        if (difficulty === 3) {
          confetti({
            particleCount: 40,
            angle: 60,
            spread: 55,
            origin: {x: 0},
          });
          confetti({
            particleCount: 40,
            angle: 120,
            spread: 55,
            origin: {x: 1},
          });
        }
        confetti({
          particleCount: 40,
          spread: 135,
        });

        if (Date.now() < end) {
          requestAnimationFrame(frame);
        }
      })();
    } else {
      confetti({
        particleCount,
        spread: 135,
      });
    }
  }, []);
  const nextLevel = () => {
    trackEvent('click', 'general', 'button', 'next-level');
    updateDifficulty(difficulty - 2);
  };
  const share = async () => {
    trackEvent('click', 'general', 'button', 'share');
    const squares = [];
    for (let rowIndex = 0; rowIndex < 5; rowIndex++) {
      for (let columnIndex = 0; columnIndex < 5; columnIndex++) {
        const key = `${rowIndex}-${columnIndex}`;
        squares.push(key in fixedCells ? '⬛' : '⬜');
      }
      if (rowIndex !== 4) {
        squares.push('\n');
      }
    }
    const text = `Word Crunch #${puzzleNumber}\n${
      difficultyLabelMap[difficulty]
    } ${star.repeat(1 + (9 - difficulty) / 2)}\n${squares.join('')}`;
    const shareData = {
      title: 'Word Crunch',
      text,
    };

    try {
      if (
        navigator.userAgent.toLowerCase().match(/mobile/i) &&
        typeof navigator.share === 'function'
      ) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(shareData.text);
        setCopiedToClipboard(true);
      }
    } catch (e: unknown) {
      //
    }
  };
  const highestSolveCount = Math.max(...Object.values(myStats.hardestSolve));
  const getBarWidth = (count: number) => (100 * count) / highestSolveCount;
  const labels: Record<number, string> = {
    9: 'Easy',
    7: 'Medium',
    5: 'Hard',
    3: 'Brutal',
  };
  const bars = [9, 7, 5, 3].map(statDifficulty => {
    const count = myStats.hardestSolve[statDifficulty];
    const barWidth = getBarWidth(count);
    const showValueInsideBar = barWidth > 10;
    return (
      <tr key={statDifficulty}>
        <td
          className={`${streakLabel} ${
            statDifficulty === difficulty ? streakLabelCurrent : ''
          }`}
        >
          {labels[statDifficulty]}
        </td>
        <td className={streakValue}>
          <div
            className={`${streakBar} ${
              statDifficulty === difficulty ? streakBarCurrent : ''
            } ${barWidth === 0 ? zeroWidth : ''}`}
            style={{width: `${barWidth}%`}}
          >
            {showValueInsideBar ? count : null}
          </div>
          {showValueInsideBar ? null : count}
        </td>
      </tr>
    );
  });
  return (
    <div className={container}>
      <h2>{phraseMap[difficulty]}</h2>
      <div>
        <div className={mainInfo}>
          <div>
            <div>
              You solved <b>{difficultyLabelMap[difficulty]}</b>
            </div>
            <div className={stars}>
              <StarGroup empty={0} filled={1 + (9 - difficulty) / 2} />
            </div>
          </div>
          {puzzleDate ? <DateCalendar date={puzzleDate} /> : null}
        </div>
        <p className={buttonContainer}>
          {difficulty === 3 ? (
            <span>
              <IconButton
                icon="icon_rankings.png"
                text=""
                onClick={() => {
                  trackEvent(
                    'click',
                    'general',
                    'menu',
                    OPENED_MODAL.LEADERBOARDS,
                  );
                  onSetOpenedModal(OPENED_MODAL.LEADERBOARDS);
                }}
              />
            </span>
          ) : (
            <span>
              <IconButton
                icon="btn_icon_puzzle.png"
                text="Next Level"
                onClick={nextLevel}
              />
            </span>
          )}
          {copiedToClipboard ? (
            <span className={clipboardMessage}>
              <Check />
              Copied to clipboard.
            </span>
          ) : (
            <span>
              <IconButton
                icon="btn_icon_share.png"
                text="Share"
                onClick={share}
              />
            </span>
          )}
        </p>
        {difficulty === 3 ? (
          <span>
            You&apos;ve solved all of the puzzles for today. Come back for more
            tomorrow!
          </span>
        ) : null}
        <div className={victoryStatsContainer}>
          <h4 className={scoresLabel}>Stats</h4>
          <div className={statsContainer}>
            <div>
              <div className={statValue}>{myStats.daysPlayed}</div>
              <div className={statLabel}>Days Played</div>
            </div>
            <div>
              <div className={statValue}>{myStats.daysSolved}</div>
              <div className={statLabel}>Days With a Solve</div>
            </div>
            <div>
              <div className={statValue}>{myStats.currentStreak}</div>
              <div className={statLabel}>Current Streak</div>
            </div>
            <div>
              <div className={statValue}>{myStats.maxStreak}</div>
              <div className={statLabel}>Max Streak</div>
            </div>
          </div>
          <h4 className={scoresLabel}>Hardest Solve</h4>
          <table className={streakTable}>
            <tbody>{bars}</tbody>
          </table>
        </div>
        <p className="textAlignCenter">
          This puzzle has been solved by {gameStats[0]} out of {gameStats[1]}{' '}
          players. You&apos;ve done better than {percentBetterThan}% of players.
        </p>
        <p className="textAlignCenter">
          <Circle fillPercent={percentBetterThan} />
        </p>
      </div>
    </div>
  );
};
